import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Switch,
  Chip,
  Collapse,
  Pagination,
} from "@mui/material";
import { Add, Edit, Delete, HelpOutline, ExpandMore, ExpandLess } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { instituteFaqService } from "../../services/instituteFaqService";
import { usePermissions } from "../../hooks/usePermissions";

const emptyForm = {
  question: "",
  answer: "",
  sortOrder: 0,
  mediaUrl: "",
  mediaType: "none",
};

function normalizeRole(user) {
  const raw = user?.role?.slug ?? user?.role ?? "";
  if (typeof raw === "string") return raw.toLowerCase().trim();
  if (raw && typeof raw === "object" && raw.slug) return String(raw.slug).toLowerCase().trim();
  return String(raw).toLowerCase().trim();
}

function parseApiError(e, fallback) {
  const d = e?.response?.data;
  if (typeof d?.message === "string" && d.message) return d.message;
  if (typeof d?.error === "string" && d.error) return d.error;
  if (typeof d === "string" && d) return d;
  if (e?.message) return e.message;
  return fallback;
}

const InstituteFaqManagement = () => {
  const user = useSelector((state) => state.auth?.user);
  const normalizedRole = useMemo(() => normalizeRole(user), [user]);
  const isSuperAdmin = normalizedRole === "superadmin";
  const selectedInstitute = useSelector((state) => state.instituteContext?.selectedInstitute);
  const { hasPermission } = usePermissions();
  const canCreateFaq = isSuperAdmin || hasPermission("faq.create");
  const canUpdateFaq = isSuperAdmin || hasPermission("faq.update");
  const canDeleteFaq = isSuperAdmin || hasPermission("faq.delete");

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 0 });
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /** Target institute for all FAQ API calls (from JWT for institute users; from context for super admin). */
  const resolvedInstituteId = useMemo(() => {
    const raw =
      user?.instituteId ?? user?.institute?.id ?? selectedInstitute?.id ?? null;
    if (raw == null || raw === "") return null;
    const n = Number.parseInt(String(raw), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedInstitute?.id, user?.instituteId, user?.institute?.id]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: search.trim() || undefined,
        // page and limit are optional - backend defaults to page=1, limit=50
        // Uncomment below if you want to send specific page/limit
        // page: pagination.page,
        // limit: pagination.limit
      };
      if (resolvedInstituteId != null) {
        params.instituteId = resolvedInstituteId;
      }
      const res = await instituteFaqService.list(params);
      const payload = res?.data?.data ?? res?.data;
      const list = Array.isArray(payload?.faqs) ? payload.faqs : [];
      setRows(list);
      if (payload?.pagination) {
        setPagination(prev => ({ ...prev, ...payload.pagination }));
      }
    } catch (e) {
      toast.error(parseApiError(e, "Failed to load FAQs"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, resolvedInstituteId]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchList();
    }, 300);
    return () => clearTimeout(t);
  }, [fetchList]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSaveError("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      question: row.question || "",
      answer: row.answer || "",
      sortOrder: row.sortOrder ?? 0,
      mediaUrl: row.mediaUrl || "",
      mediaType: row.mediaType || "none",
    });
    setSaveError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setSaveError("");
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaveError("");
    if (!form.question?.trim() || !form.answer?.trim()) {
      setSaveError("Question and answer are required.");
      toast.error("Question and answer are required");
      return;
    }
    if (isSuperAdmin && resolvedInstituteId == null) {
      const msg = "Open an institute from Super Admin → Manage Institute to manage its FAQs.";
      setSaveError(msg);
      toast.error(msg);
      return;
    }
    setSaving(true);
    try {
      const sortParsed = Number.parseInt(String(form.sortOrder ?? 0), 10);
      const body = {
        question: form.question.trim(),
        answer: form.answer.trim(),
        sortOrder: Number.isFinite(sortParsed) ? sortParsed : 0,
        mediaUrl: form.mediaUrl?.trim() || null,
        mediaType: form.mediaType || "none",
      };
      if (resolvedInstituteId != null) {
        body.instituteId = resolvedInstituteId;
      }
      if (editingId) {
        await instituteFaqService.update(editingId, body);
        toast.success("FAQ updated");
      } else {
        await instituteFaqService.create(body);
        toast.success("FAQ created");
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      await fetchList();
    } catch (e) {
      const msg = parseApiError(e, "Save failed");
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (isSuperAdmin && resolvedInstituteId == null) {
      toast.error("Open an institute from Super Admin → Manage Institute first.");
      return;
    }
    if (!window.confirm("Delete this FAQ?")) return;
    setDeletingId(row.id);
    try {
      const axiosConfig =
        resolvedInstituteId != null
          ? { params: { instituteId: resolvedInstituteId } }
          : undefined;
      await instituteFaqService.remove(row.id, axiosConfig);
      toast.success("Deleted");
      await fetchList();
    } catch (e) {
      toast.error(parseApiError(e, "Delete failed"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (row) => {
    if (isSuperAdmin && resolvedInstituteId == null) {
      toast.error("Open an institute from Super Admin → Manage Institute first.");
      return;
    }
    try {
      await instituteFaqService.update(row.id, { isActive: !row.isActive });
      toast.success(`FAQ ${!row.isActive ? "activated" : "deactivated"}`);
      await fetchList();
    } catch (e) {
      toast.error(parseApiError(e, "Update failed"));
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <HelpOutline color="primary" />
        <Typography variant="h5" fontWeight={600}>
          Help &amp; Support — FAQs
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These entries appear in the mobile app Help &amp; Support screen for your institute.
      </Typography>

      {isSuperAdmin && !resolvedInstituteId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Open an institute from <strong>Super Admin → Manage Institute</strong> to add or edit FAQs
          for that institute.
        </Alert>
      )}

      {selectedInstitute?.name && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Managing FAQs for: <strong>{selectedInstitute.name}</strong>
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Question or answer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        {canCreateFaq ? (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Add FAQ
          </Button>
        ) : null}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell width={72}>Order</TableCell>
              <TableCell>Question</TableCell>
              <TableCell width={120}>Active</TableCell>
              <TableCell width={120} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  No FAQs yet. Add one for your institute.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                      >
                        {expandedRow === row.id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{row.sortOrder}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.question}
                      </Typography>
                      {row.mediaType && row.mediaType !== "none" && (
                        <Chip label={row.mediaType} size="small" sx={{ mt: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={row.isActive}
                        onChange={() => handleToggleActive(row)}
                        size="small"
                        color="primary"
                        disabled={!canUpdateFaq}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canUpdateFaq ? (
                        <IconButton size="small" onClick={() => openEdit(row)} aria-label="edit">
                          <Edit fontSize="small" />
                        </IconButton>
                      ) : null}
                      {canDeleteFaq ? (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(row)}
                          aria-label="delete"
                          disabled={deletingId === row.id}
                        >
                          {deletingId === row.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Delete fontSize="small" />
                          )}
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedRow === row.id ? undefined : 0 }}>
                      <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2, bgcolor: "grey.50" }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Answer:
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                            {row.answer}
                          </Typography>
                          {row.mediaUrl && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Media: {row.mediaUrl}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={(e, page) => setPagination(prev => ({ ...prev, page }))}
            color="primary"
          />
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (saving) return;
          setDialogOpen(false);
          setSaveError("");
        }}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSave}>
          <DialogTitle>{editingId ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
          <DialogContent>
            {saveError ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError("")}>
                {saveError}
              </Alert>
            ) : null}
            <TextField
              fullWidth
              margin="normal"
              label="Question"
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              required
              disabled={saving}
              autoFocus
            />
            <TextField
              fullWidth
              margin="normal"
              label="Answer"
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              multiline
              minRows={4}
              required
              disabled={saving}
            />
            <TextField
              fullWidth
              margin="normal"
              type="number"
              label="Sort order"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
              disabled={saving}
              inputProps={{ min: 0 }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Media URL (optional)"
              value={form.mediaUrl}
              onChange={(e) => setForm({ ...form, mediaUrl: e.target.value })}
              disabled={saving}
              placeholder="https://example.com/image.jpg"
              helperText="Image or video URL to display with this FAQ"
            />
            <TextField
              fullWidth
              margin="normal"
              select
              label="Media Type"
              value={form.mediaType}
              onChange={(e) => setForm({ ...form, mediaType: e.target.value })}
              disabled={saving}
              SelectProps={{ native: true }}
            >
              <option value="none">None</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button type="button" onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default InstituteFaqManagement;
